import { useTranslation } from "react-i18next";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { AdminMrrStatsDB } from "@KassaroAB/shared/types/adminMrrStats";
import type { Money } from "@KassaroAB/shared/types/money";
import {
  multiplyMoney,
  subtractMoney,
  zeroMoney,
} from "@KassaroAB/shared/utils/money";
import { formatMoney } from "../../utils/formatMoney";
import { formatSigned } from "../../utils/formatSigned";
import { getMoneyForCurrency, isSalesToday } from "../../utils/dailySalesUtils";
import { StatBox } from "../../components/StatBox";
import { Row } from "../../components/Row";
import { StatChangeFooter, type StatChange } from "./StatChangeFooter";
import { spacing } from "@styling/sizing";

const MONTHS_PER_YEAR = 12;

const MONEY_METRICS: { key: "mrr" | "arr"; months: number }[] = [
  { key: "mrr", months: 1 },
  { key: "arr", months: MONTHS_PER_YEAR },
];

const getCurrencies = (
  dayStats: AdminMrrStatsDB | undefined,
  comparisonStats: AdminMrrStatsDB | undefined
): Currency[] =>
  Object.values(Currency).filter(
    (currency) =>
      currency === Currency.SEK ||
      getMoneyForCurrency(dayStats?.mrrByCurrency ?? {}, currency) ||
      getMoneyForCurrency(comparisonStats?.mrrByCurrency ?? {}, currency)
  );

const getMrrForCurrency = (
  stats: AdminMrrStatsDB,
  currency: Currency
): Money =>
  getMoneyForCurrency(stats.mrrByCurrency, currency) ?? zeroMoney(currency);

interface MrrValueBoxesProps {
  date: string;
  selectedDateLabel: string;
  comparisonDateLabel: string;
  dayStats: AdminMrrStatsDB | undefined;
  comparisonStats: AdminMrrStatsDB | undefined;
}

export const MrrValueBoxes = ({
  date,
  selectedDateLabel,
  comparisonDateLabel,
  dayStats,
  comparisonStats,
}: MrrValueBoxesProps) => {
  const { t, i18n } = useTranslation();
  const isToday = isSalesToday(date);
  const percentFormat = new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: 1,
  });

  const label = (metric: "mrr" | "arr" | "payingStores", currency?: string) =>
    isToday
      ? t(`adminMrrStats.${metric}Today`, { currency })
      : t(`adminMrrStats.${metric}OnDate`, { currency, date: selectedDateLabel });

  const getMoneyChange = (current: Money, previous: Money): StatChange => {
    const difference = subtractMoney(current, previous);
    const percent =
      previous.amount === 0
        ? ""
        : ` (${formatSigned(
            difference.amount,
            percentFormat.format((difference.amount / previous.amount) * 100)
          )} %)`;
    return {
      value: difference.amount,
      label: `${formatSigned(difference.amount, formatMoney(difference))}${percent}`,
    };
  };

  const storesDifference =
    dayStats && comparisonStats
      ? dayStats.payingStoresCount - comparisonStats.payingStoresCount
      : null;

  return (
    <Row $gap={spacing.x2}>
      {getCurrencies(dayStats, comparisonStats).flatMap((currency) =>
        MONEY_METRICS.map(({ key, months }) => {
          const current =
            dayStats &&
            multiplyMoney(getMrrForCurrency(dayStats, currency), months);
          const previous =
            comparisonStats &&
            multiplyMoney(getMrrForCurrency(comparisonStats, currency), months);
          return (
            <StatBox
              key={`${key}-${currency}`}
              label={label(key, currency)}
              value={current ? formatMoney(current) : "-"}
              footer={
                <StatChangeFooter
                  change={
                    current && previous
                      ? getMoneyChange(current, previous)
                      : null
                  }
                  comparisonDateLabel={comparisonDateLabel}
                />
              }
            />
          );
        })
      )}
      <StatBox
        label={label("payingStores")}
        value={dayStats?.payingStoresCount ?? "-"}
        footer={
          <StatChangeFooter
            change={
              storesDifference === null
                ? null
                : {
                    value: storesDifference,
                    label: formatSigned(storesDifference),
                  }
            }
            comparisonDateLabel={comparisonDateLabel}
          />
        }
      />
    </Row>
  );
};
